import React, {useMemo} from 'react';
import moment from 'moment';

import bannerIcon from './images/bannerIcon.svg'
import locationIcon from './images/locationIcon.svg'
import indicatorStrengthIcon from './images/indicatorStrengthIcon.svg'
import percentageGoalIcon from './images/percentageGoalIcon.svg'
import timeGoalIcon from './images/timeGoalIcon.svg'
import risingStarIcon from './images/risingStarIcon.svg'
import topLandlordIcon from './images/topLandlordIcon.svg'
import newLandlordIcon from './images/newLandlordIcon.svg'
import './style.less'
import {useTranslation} from 'react-i18next';

const componentLabel = 'main_page.dashboard.top_landlord'

const TopLandlordHeader = () => {
  const {t} = useTranslation();

  return (
    <div className={'TopLandlordHeader'}>
      <img src={topLandlordIcon}/>
      <h4>{t(componentLabel + '.title')}</h4>
      <p>{t(componentLabel + '.description')}</p>
    </div>
  )
}

//objective_type diz como se lê o valor do indicador: "number" é uma contagem (reservas
//confirmadas), "percentage" uma taxa e "days" um número de dias sem uma dada acção.
const formatIndicatorValue = (indicator, value, t) => {
  if (value === null || value === undefined) return null;
  switch (indicator.objective_type) {
    case 'days':
      return `${value} ${t(componentLabel + '.days_unit')}`;
    case 'number':
      return `${value}`;
    default:
      return `${value}%`;
  }
}

//os indicadores invertidos são os que se querem baixos. A taxa de cancelamento tem de ficar
//mesmo abaixo do objectivo, enquanto os prazos em dias ainda cumprem em cima dele.
const getComparator = (indicator) => {
  if (indicator.direction !== 'reversed') return '≥';
  return indicator.objective_type === 'days' ? '≤' : '<';
}

//cumpre com o mesmo critério do símbolo mostrado ao lado do objectivo, para a cor da barra
//nunca contradizer o que está escrito
const isGoalMet = (indicator) => {
  switch (getComparator(indicator)) {
    case '<':
      return indicator.current < indicator.objective;
    case '≤':
      return indicator.current <= indicator.objective;
    default:
      return indicator.current >= indicator.objective;
  }
}

const ARROW_OFFSETS = [9, 33.5, 58, 82.5, 107];

//setas desenhadas aqui e não num .svg importado para poderem ser pintadas pelo css com a cor
//do estado do indicador. Apontam no sentido em que o indicador melhora.
const DirectionArrows = ({reversed}) => (
  <svg className={`arrowIcons ${reversed ? 'reversed' : ''}`} width="123" height="25" viewBox="0 0 123 25"
       xmlns="http://www.w3.org/2000/svg">
    {ARROW_OFFSETS.map(x => <path key={x} d={`M${x} 5.5L${x + 7} 12.5L${x} 19.5Z`}/>)}
  </svg>
)

const ProgressBarWithIcons = ({indicator}) => {

  const {t} = useTranslation();

  const hasValue = indicator.current !== null && indicator.current !== undefined;

  const goalStatus = hasValue ? (isGoalMet(indicator) ? 'goal_met' : 'goal_missed') : '';

  //a bandeira do objectivo é posicionada a partir do próprio objectivo e da escala do
  //indicador, em vez de assumir que todos os objectivos são 80% ou 50%
  const goalPercentage = useMemo(() => {
    if (!indicator.max) return 0;
    const value = (indicator.objective / indicator.max) * 100;
    return Math.min(Math.max(value, 0), 100);
  }, [indicator]);

  const completedPercentage = useMemo(() => {
    if (!hasValue || !indicator.max) return 0;
    return indicator.current < indicator.max ? (indicator.current / indicator.max * 100) : 102.6;
  }, [indicator, hasValue]);

  const completedLabel = useMemo(() => formatIndicatorValue(indicator, indicator.current, t), [indicator, t]);

  const styles = {
    completed: {
      width: `${completedPercentage ? completedPercentage : 0}%`
    },
    currentPositionIcon: {
      left: `calc(${completedPercentage ? completedPercentage : 0}% - 33px)`
    },
    //metade da largura do círculo (36px), para ficar centrado no objectivo
    goalPositionIcon: {
      left: `calc(${goalPercentage}% - 18px)`
    }
  }

  return (
    <div className={`ProgressBarWithIcons center ${goalStatus}`}>
      <div className={`ProgressBarWithIcons__bar ${!completedPercentage ? 'no_data' : ''}`}>
        <span className={'ProgressBarWithIcons__completed'} style={styles.completed}/>
      </div>
      <div className={'goalAchieved center'} style={styles.goalPositionIcon}>
        <div className={'ProgressBarIcon center'}>
          <img src={bannerIcon}/>
        </div>
      </div>

      <div className={'currentPosition'} style={styles.currentPositionIcon}>
        <h6>{t('you')}</h6>

        <div className={'ProgressBarIcon center'}>
          <img src={locationIcon}/>
        </div>

        {/* zero é um valor real (zero reservas confirmadas), só a ausência de valor é "sem dados" */}
        {hasValue ?
          <h5>{completedLabel}</h5> : <p className={'small'}>{t('no_data')}</p>}

      </div>

      <DirectionArrows reversed={indicator.direction === 'reversed'}/>
    </div>
  )
}

//objective_type tem 3 valores possiveis "number", "percentage" ou "days"
//indicatorStrength tem 3 valores possiveis "rising_star", "top_landlord" ou vazio
const TopLandlordIndicatorContainer = ({indicator}) => {

  const {t} = useTranslation();

  const isDays = indicator.objective_type === 'days';

  return (
    <div className={'TopLandlordIndicatorContainer basic_container'}>
      <div>
        <div className={'TopLandlordIndicatorBox'}>
          <h6>{t(componentLabel + '.indicator_label')}</h6>
          <p className={'small'}>{t(componentLabel + '.indicators.' + indicator.type)}</p>
        </div>
          {indicator.strength &&
            <div className={'TopLandlordIndicatorBox indicatorStrength'}>
              <h6>{t(componentLabel + '.indicator_strength_label')}</h6>
              <div>
                  <p className={'small'}>{t(componentLabel + '.rank.' + indicator.strength + '.designation')}</p>
                      <img src={indicatorStrengthIcon}/>
              </div>
            </div>}
        <div className={'TopLandlordIndicatorBox'}>
          <h6>{t(componentLabel + '.goal_label')}</h6>
          <div>
            <p className={'small'}>{`${getComparator(indicator)} ${formatIndicatorValue(indicator, indicator.objective, t)}`}</p>
            <img src={isDays ? timeGoalIcon : percentageGoalIcon}/>
          </div>
        </div>
      </div>
      <ProgressBarWithIcons indicator={indicator}/>
    </div>
  )
}

//o nível só se perde ao fim do período de graça, por isso enquanto ele corre mostra-se ao
//senhorio quanto tempo lhe resta para repor os indicadores
const TopLandlordGraceWarning = ({grace, rank}) => {
  const {t} = useTranslation();

  if (!grace) return null;

  const levelName = t(componentLabel + '.rank.' + rank + '.designation');

  return (
    <div className={'TopLandlordGraceWarning'}>
      <h5>{t(componentLabel + '.grace.title', {level: levelName})}</h5>
      <p>{t(componentLabel + '.grace.description', {
        days: grace.daysRemaining,
        date: moment(grace.endsAt).format('DD/MM/YYYY')
      })}</p>
    </div>
  )
}

const TopLandlordRank = ({rank, user}) => {
  const {t} = useTranslation();

  const icon = rank === 'top_landlord' ? topLandlordIcon : rank === 'rising_star' ? risingStarIcon : newLandlordIcon;

  return (
    <div className={'TopLandlordRank basic_container'}>
      <div className={`DashboardIcon ${rank}`}>
        <img src={icon}/>
      </div>
      <h5>{`${user} ${t(componentLabel + '.rank.' + rank + '.title')}`}</h5>
      <p>{t(componentLabel + '.rank.' + rank + '.description')}</p>
    </div>
  )
}

const TopLandlordSection = ({user, indicators, status, grace}) => {


  return (
    <div className={'TopLandLordSection container'}>
      <TopLandlordHeader/>

      {/* a graça só existe para quem tem nível a perder */}
      {grace && status !== 'new_landlord' && <TopLandlordGraceWarning grace={grace} rank={status}/>}

      <div className={'TopLandlordIndicators'}>
        {indicators?.map(indicator => (
          <TopLandlordIndicatorContainer key={indicator.type} indicator={indicator}/>
        ))}
      </div>

        {status && <TopLandlordRank rank={status} user={user?.name?.split(' ')?.[0]}/>}
    </div>
  )
}



export default TopLandlordSection;
